import * as React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import { formatTimeLocalHHmm, parseTimeLocalToPickerDate } from '@packages/core';

import { DatePicker, type DatePickerProps } from '../composites/DatePicker';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { RhfFormFieldShellProps, RhfLayoutProps } from './rhfLayout';

export interface RhfTimePickerProps
  extends Omit<
      DatePickerProps,
      'value' | 'onChange' | 'invalid' | 'name' | 'showTimeSelect' | 'showTimeSelectOnly'
    >,
    RhfLayoutProps,
    RhfFormFieldShellProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
  id?: string;
  /**
   * Native `<input type="time">` step in seconds — mapped to `timeIntervals` (minutes) when
   * `timeIntervals` is not set. Default 900 → 15 minutes (legacy job scheduler parity).
   */
  step?: number;
}

/**
 * Time picker for React Hook Form — legacy parity via DS DatePicker (react-datepicker
 * time-only list), not the browser native time control.
 */
export const RhfTimePicker = React.forwardRef<HTMLDivElement, RhfTimePickerProps>(
  function RhfTimePicker(
    {
      control,
      name,
      label,
      hint,
      tooltip,
      required = false,
      semanticId,
      disabled = false,
      controlWidth,
      step = 900,
      timeIntervals: timeIntervalsProp,
      format = 'h:mm aa',
      placeholder = 'HH:MM',
      calendarIcon = 'access_time',
      timeCaption = 'Time',
      ...rest
    },
    ref,
  ) {
    const timeIntervals =
      timeIntervalsProp ?? (step >= 60 ? Math.round(step / 60) : 15);

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
          const dateValue = parseTimeLocalToPickerDate(field.value);

          return (
            <FormField
              ref={ref}
              label={label}
              htmlFor={name}
              hint={hint}
              tooltip={tooltip}
              required={required}
              semanticId={semanticId}
              disabled={disabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <DatePicker
                name={name}
                value={dateValue}
                onChange={(date) => {
                  if (date == null) {
                    field.onChange('');
                    return;
                  }
                  field.onChange(formatTimeLocalHHmm(date));
                }}
                onInputBlur={field.onBlur}
                invalid={Boolean(errorText)}
                disabled={disabled}
                semanticId={semanticId}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={timeIntervals}
                timeCaption={timeCaption}
                format={format}
                placeholder={placeholder}
                calendarIcon={calendarIcon}
                {...rest}
              />
            </FormField>
          );
        }}
      />
    );
  },
);

RhfTimePicker.displayName = 'RhfTimePicker';
